import { useEffect, useState } from 'react';

import { type APIError, idpApi, type ProfileResponse } from '../services/idp-api';

type UseIdpSessionState = {
  isAuthenticated: boolean;
  identity: ProfileResponse | null;
  isLoading: boolean;
  error: APIError | Error | null;
};

export function useIdpSession(): UseIdpSessionState {
  const [identity, setIdentity] = useState<ProfileResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<APIError | Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadProfile() {
      setIsLoading(true);
      setError(null);

      try {
        const profile = await idpApi.getProfile();
        if (!isMounted) {
          return;
        }

        setIdentity(profile);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        setIdentity(null);
        if (loadError instanceof Error) {
          setError(loadError);
        } else {
          setError(new Error('Failed to load session'));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    void loadProfile();

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    isAuthenticated: Boolean(identity),
    identity,
    isLoading,
    error,
  };
}
